import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePwaInstall } from "@/hooks/usePwaInstall";

interface InstallAppBannerProps {
  onWaitlistClick: () => void;
}

const DISMISS_KEY = "ayeris_install_banner_dismissed";

const InstallAppBanner = ({ onWaitlistClick }: InstallAppBannerProps) => {
  const { t } = useTranslation();
  const { canPrompt, isInstalled, isIos, isStandalone, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(DISMISS_KEY));
  const [expanded, setExpanded] = useState(false);

  if (isStandalone || isInstalled || dismissed) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (canPrompt) {
      await promptInstall();
    }
  };

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="relative rounded-2xl bg-card border border-border overflow-hidden"
    >
      {/* Collapsed header — always visible, acts as toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
          <Download className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {t("install.title", "Add Ayeris to Your Home Screen")}
          </p>
          {!expanded && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {t("install.tapToExpand", "Tap to learn more")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("install.subtitle", "No download needed! Add Ayeris to your home screen for instant access anytime. You can always find this option in settings later.")}
              </p>
              <p className="text-[11px] text-muted-foreground/70 leading-relaxed italic">
                {t("install.noInstall", "This doesn't install anything on your device — it simply adds a shortcut to your home screen that opens Ayeris in its own window.")}
              </p>

              {/* Install action */}
              {canPrompt ? (
                <button
                  onClick={handleInstall}
                  className="w-full py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t("install.addToHomeScreen", "Add to Home Screen")}
                </button>
              ) : isIos ? (
                <div className="p-3 rounded-xl bg-secondary/50 border border-border">
                  <p className="text-xs text-muted-foreground text-center leading-relaxed">
                    {t("install.iosInstructions", "Tap the")} <Share className="w-3.5 h-3.5 inline-block mx-0.5 -mt-0.5" /> {t("install.iosShare", "share button in Safari, then tap \"Add to Home Screen\"")}
                  </p>
                </div>
              ) : (
                <div className="p-3 rounded-xl bg-secondary/50 border border-border">
                  <p className="text-xs text-muted-foreground text-center leading-relaxed">
                    {t("install.browserInstructions", "Use your browser menu to \"Add to Home Screen\" or \"Install App\"")}
                  </p>
                </div>
              )}

              {/* Native app waitlist CTA */}
              <div className="pt-1 border-t border-border">
                <p className="text-[11px] text-muted-foreground text-center leading-relaxed mt-2">
                  {t("install.nativeAppTeaser", "Want a dedicated app? We're planning native iOS & Android apps — the more people who register interest, the more likely it'll happen (and sooner!)")}
                </p>
                <button
                  onClick={onWaitlistClick}
                  className="w-full mt-2 py-2 rounded-xl bg-secondary text-foreground font-medium text-xs border border-border active:scale-95 transition-transform"
                >
                  {t("install.registerInterest", "Register Your Interest")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InstallAppBanner;
